import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faUserPlus,
  faArrowRight,
  faTrash,
  faCommentMedical,
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight
} from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchPatients } from '../../store/clinics/clinicsSlice';
import { clinicsService, type Patient } from '../../services/clinicsService';
import { Link, useNavigate } from 'react-router-dom';
import ClinicsNewPatientModal from './ClinicsNewPatientModal';
import { GhostSelect } from '../../components/ui/GhostSelect';
import { tagsApi } from '../../store/tags/tagsApi';
import { TagBadge } from '../../components/ui/TagBadge';
import BtnTaratic from '../../components/ui/BtnTaratic';
import { useDeleteConfirmation } from '../../components/ui/DeleteConfirmationProvider';

const ClinicsPatientsPage: React.FC = () => {
  const { t } = useTranslation();
  const confirmDelete = useDeleteConfirmation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { patients, totalPatients, loading } = useAppSelector((state) => state.clinics);

  const [page, setPage] = useState(1);
  const [inputPage, setInputPage] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const perPage = 10;

  const { data: tagsResponse } = tagsApi.useGetTagsWithFoldersQuery();
  const globalTags = tagsResponse?.tags || [];

  const totalPages = Math.max(1, Math.ceil(totalPatients / perPage));

  const loadPatients = useCallback(() => {
    dispatch(
      fetchPatients({
        page,
        per_page: perPage,
        search: searchTerm,
        status: statusFilter,
        tag_ids: tagFilter.length > 0 ? tagFilter.join(',') : undefined
      })
    );
  }, [dispatch, page, perPage, searchTerm, statusFilter, tagFilter]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  // Keep input page state in sync with current page number
  useEffect(() => {
    setInputPage(String(page));
  }, [page]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadPatients();
  };

  const handleJumpPage = (e: React.FormEvent) => {
    e.preventDefault();
    const p = parseInt(inputPage, 10);
    if (!isNaN(p)) {
      const validPage = Math.min(Math.max(1, p), totalPages);
      setPage(validPage);
      setInputPage(String(validPage));
    } else {
      setInputPage(String(page));
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);

      if (page <= 3) {
        end = 4;
      }
      if (page >= totalPages - 2) {
        start = totalPages - 3;
      }

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push('...');
      }

      pages.push(totalPages);
    }
    return pages;
  };

  const handleDelete = async (patient: Patient) => {
    if (await confirmDelete({
      title: 'Eliminar paciente',
      message: `¿Estás seguro de que deseas eliminar a ${patient.full_name}?`,
    })) {
      try {
        await clinicsService.deletePatient(patient.id);
        loadPatients();
      } catch (err) {
        alert('Error al eliminar paciente.');
      }
    }
  };

  const startItem = totalPatients === 0 ? 0 : (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, totalPatients);

  return (
    <div data-ui="clinics-patients-page" className="page-theme-surface page-theme-clinics flex flex-col h-full w-full p-8 gap-8 overflow-y-auto custom-scrollbar relative">
      <div data-ui="clinics-patients-page-region-001" className="flex flex-wrap items-center justify-between gap-4 relative z-10">
        <div data-ui="clinics-patients-page-region-002" className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-white tracking-wide">{t('clinics.patients.title')}</h1>
          <p className="text-white font-medium">Gestiona tu lista de pacientes y sus historiales clínicos</p>
        </div>
        <BtnTaratic data-tour="create-patient" onClick={() => setIsModalOpen(true)} color="blue" size="md" className="flex items-center gap-2">
          <FontAwesomeIcon icon={faUserPlus} />
          <span className="whitespace-nowrap">{t('clinics.patients.newPatient')}</span>
        </BtnTaratic>
      </div>

      <div data-ui="clinics-patients-page-region-003" className="rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md overflow-hidden relative z-10 flex-1 min-h-0 flex flex-col">
        {/* Search & Filters Header */}
        <div data-ui="clinics-patients-page-region-004" className="px-4 py-3 border-b border-white/20 bg-black/75 flex flex-col sm:flex-row gap-3 items-center justify-between shrink-0">
          <form onSubmit={handleSearch} className="relative w-full sm:w-72 max-w-md">
            <div data-ui="clinics-patients-page-region-005" className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={faSearch} className="text-white/70" />
            </div>
            <input
              type="text"
              placeholder={t('clinic.patients.search.searchPlaceholder', t('clinics.patients.searchPlaceholder'))}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-black/20 border border-white/20 text-white text-sm rounded-full focus:outline-none focus:ring-blue-400 focus:border-blue-400 block pl-11 p-2.5 placeholder-white/60 shadow-inner transition-all duration-300 hover:bg-black hover:shadow-inner-glow hover:shadow-inner-glow-white-2xl focus:bg-black focus:shadow-inner-glow focus:shadow-inner-glow-white-2xl"
            />
          </form>

          <div data-ui="clinics-patients-page-region-006" className="flex w-full sm:w-auto items-center gap-2 flex-wrap sm:flex-nowrap">
            <GhostSelect
              value={statusFilter}
              onChange={(val: string) => {
                setStatusFilter(val);
                setPage(1);
              }}
              className="w-full sm:w-36"
              options={[
                { value: '', label: t('clinics.appointments.filters.all') || 'Todos' },
                { value: 'active', label: t('clinics.dashboard.status.active') || 'Activo' },
                { value: 'inactive', label: t('clinics.dashboard.status.inactive') || 'Inactivo' },
                { value: 'discharged', label: t('clinics.dashboard.status.discharged') || 'Dado de alta' }
              ]}
            />
            <GhostSelect
              multiple={true}
              value={tagFilter}
              onChange={(val: string[]) => {
                setTagFilter(val);
                setPage(1);
              }}
              className="w-full sm:w-56"
              placeholder="Todas las etiquetas"
              options={[
                ...globalTags.map((tag) => ({
                  value: tag.id.toString(),
                  label: <TagBadge tag={tag} size="sm" />,
                  textLabel: tag.name
                }))
              ]}
            />
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div data-ui="clinics-patients-page-region-007" className="flex-1 flex justify-center items-center py-12">
            <div data-ui="clinics-patients-page-region-008" className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
          </div>
        ) : patients.length === 0 ? (
          <div data-ui="clinics-patients-page-region-009" className="flex-1 flex justify-center items-center p-8 text-center text-sm text-white/60">
            {t('clinics.patients.noPatients')}
          </div>
        ) : (
          <div data-ui="clinics-patients-page-region-010" className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-xs sm:text-sm text-left">
              <thead className="text-[11px] sm:text-xs text-white bg-black/85 backdrop-blur-md border-b border-white/10 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2.5 font-bold">{t('clinics.patients.table.name')}</th>
                  <th className="px-4 py-2.5 font-bold">{t('clinics.patientDetail.info.contact')}</th>
                  <th className="px-4 py-2.5 font-bold">Tags</th>
                  <th className="px-4 py-2.5 font-bold">{t('clinics.patients.table.status')}</th>
                  <th className="px-4 py-2.5 font-bold text-center">{t('clinics.patientDetail.tabs.appointments')}</th>
                  <th className="px-4 py-2.5 font-bold text-center">{t('clinics.patients.table.lastVisit')}</th>
                  <th className="px-4 py-2.5 font-bold text-right">{t('clinics.patients.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {patients.map((patient) => (
                  <tr
                    key={patient.id}
                    className="hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('a')) {
                        return;
                      }
                      navigate(`/clinics/patients/${patient.id}`);
                    }}
                  >
                    <td className="px-4 py-3 font-medium text-white whitespace-nowrap">
                      <div data-ui="clinics-patients-page-region-011" className="flex items-center gap-2.5">
                        {patient.avatar_url ? (
                          <img src={patient.avatar_url} alt={patient.full_name} className="w-8 h-8 rounded-full object-cover shrink-0" />
                        ) : (
                          <div data-ui="clinics-patients-page-region-012" className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center font-bold text-xs border border-blue-500/30 shrink-0">
                            {patient.full_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div data-ui="clinics-patients-page-region-013" className="min-w-0">
                          <div data-ui="clinics-patients-page-region-014" className="truncate font-semibold">{patient.full_name}</div>
                          {patient.date_of_birth && (
                            <div data-ui="clinics-patients-page-region-015" className="text-[11px] text-white/50">{new Date(patient.date_of_birth).toLocaleDateString()}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div data-ui="clinics-patients-page-region-016" className="text-white/80">{patient.phone || '—'}</div>
                      <div data-ui="clinics-patients-page-region-017" className="text-[11px] text-white/50 max-w-[160px] truncate">{patient.email || '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div data-ui="clinics-patients-page-region-018" className="flex flex-wrap gap-1">
                        {patient.tags && patient.tags.length > 0 ? (
                          <>
                            {patient.tags.slice(0, 2).map((tag) => (
                              <TagBadge key={tag.id} tag={tag} size="xs" />
                            ))}
                            {patient.tags.length > 2 && (
                              <span
                                className="px-1.5 py-0.5 bg-black/75 border border-white/20 rounded-full text-[10px] font-bold text-white/70 shadow-sm flex items-center"
                                title={patient.tags.slice(2).map((t) => t.name).join(', ')}
                              >
                                +{patient.tags.length - 2}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-white/30 text-xs">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold border ${patient.status === 'active'
                            ? 'bg-green-500/20 border-green-500/50 text-green-300'
                            : patient.status === 'inactive'
                              ? 'bg-orange-500/20 border-orange-500/50 text-orange-300'
                              : 'bg-gray-500/20 border-gray-500/50 text-gray-300'
                          }`}
                      >
                        {patient.status === 'active'
                          ? t('clinics.dashboard.status.active')
                          : patient.status === 'inactive'
                            ? t('clinics.dashboard.status.inactive')
                            : t('clinics.dashboard.status.discharged')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-white/80 whitespace-nowrap">{patient.appointment_count || 0}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      {patient.next_appointment_date ? (
                        <div data-ui="clinics-patients-page-region-019" className="text-blue-300 font-medium">{new Date(patient.next_appointment_date).toLocaleDateString()}</div>
                      ) : (
                        <span className="text-white/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div data-ui="clinics-patients-page-region-020" className="flex justify-end gap-1.5">
                        <Link
                          data-tour="patient-chat"
                          to={`/clinics/patients/${patient.id}/chat`}
                          className="btn-taratic btn-blue btn-xs flex items-center justify-center w-7 h-7 p-0"
                          title="Chat Historial Médico"
                        >
                          <FontAwesomeIcon icon={faCommentMedical} className="text-xs" />
                        </Link>
                        <Link
                          to={`/clinics/patients/${patient.id}`}
                          className="btn-taratic btn-xs flex items-center justify-center w-7 h-7 p-0 bg-black/75 border border-white/20 hover:bg-white/10"
                          title="Ver Perfil"
                        >
                          <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        </Link>
                        <BtnTaratic
                          onClick={() => handleDelete(patient)}
                          color="red"
                          size="xs"
                          className="w-7 h-7 p-0 flex items-center justify-center"
                          title="Eliminar"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-xs" />
                        </BtnTaratic>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        <div data-ui="clinics-patients-page-region-021" className="px-4 py-2.5 border-t border-white/20 bg-black/60 flex flex-col md:flex-row items-center justify-between gap-3 shrink-0">
          <div data-ui="clinics-patients-page-region-022" className="flex items-center gap-4 text-xs text-white/70 justify-center md:justify-start md:flex-1 w-full md:w-auto">
            <span>
              Mostrando <strong className="text-white font-medium">{startItem}</strong> a{' '}
              <strong className="text-white font-medium">{endItem}</strong> de <strong className="text-white font-medium">{totalPatients}</strong> pacientes
            </span>
            <span className="hidden sm:inline-block text-white/40">|</span>
            <span className="hidden sm:inline-block text-white/60">
              Página <strong className="text-white font-medium">{page}</strong> de <strong className="text-white font-medium">{totalPages}</strong>
            </span>
          </div>

          <div data-ui="clinics-patients-page-region-023" className="flex items-center justify-center gap-3 flex-wrap sm:flex-nowrap md:flex-initial w-full md:w-auto">
            {/* Direct Jump Input */}
            <form onSubmit={handleJumpPage} className="flex items-center justify-center gap-1.5 text-xs text-white/70">
              <span className="whitespace-nowrap">Ir a pág:</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={inputPage}
                onChange={(e) => setInputPage(e.target.value)}
                onBlur={() => {
                  const p = parseInt(inputPage, 10);
                  if (isNaN(p) || p < 1 || p > totalPages) {
                    setInputPage(String(page));
                  }
                }}
                className="w-12 sm:w-14 bg-black/20 border border-white/20 rounded-lg px-2 py-1 text-center text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <BtnTaratic type="submit" color="ghost-dark" size="xs" className="px-2 py-1">
                Ir
              </BtnTaratic>
            </form>

            {/* Navigation Buttons */}
            <div data-ui="clinics-patients-page-region-024" className="flex items-center justify-center gap-1">
              <BtnTaratic
                disabled={page === 1 || loading}
                onClick={() => setPage(1)}
                color="ghost-dark"
                size="xs"
                className="px-2 py-1 disabled:opacity-40"
                title="Primera página"
              >
                <FontAwesomeIcon icon={faAnglesLeft} />
              </BtnTaratic>
              <BtnTaratic
                disabled={page === 1 || loading}
                onClick={() => setPage(page - 1)}
                color="ghost-dark"
                size="xs"
                className="px-2 py-1 disabled:opacity-40"
                title="Página anterior"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </BtnTaratic>

              <div data-ui="clinics-patients-page-region-025" className="hidden md:flex items-center gap-1">
                {getPageNumbers().map((pNum, idx) =>
                  typeof pNum === 'number' ? (
                    <BtnTaratic
                      key={idx}
                      onClick={() => setPage(pNum)}
                      color={page === pNum ? 'blue' : 'ghost-dark'}
                      size="xs"
                      className={`min-w-[28px] px-2 py-1 font-medium ${page === pNum ? 'shadow-sm' : ''}`}
                    >
                      {pNum}
                    </BtnTaratic>
                  ) : (
                    <span key={idx} className="px-1 text-white/40 text-xs select-none">
                      {pNum}
                    </span>
                  )
                )}
              </div>

              <BtnTaratic
                disabled={page >= totalPages || loading}
                onClick={() => setPage(page + 1)}
                color="ghost-dark"
                size="xs"
                className="px-2 py-1 disabled:opacity-40"
                title="Página siguiente"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </BtnTaratic>
              <BtnTaratic
                disabled={page >= totalPages || loading}
                onClick={() => setPage(totalPages)}
                color="ghost-dark"
                size="xs"
                className="px-2 py-1 disabled:opacity-40"
                title="Última página"
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </BtnTaratic>
            </div>
          </div>

          <div data-ui="clinics-patients-page-region-026" className="hidden md:flex md:flex-1 justify-end"></div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <ClinicsNewPatientModal
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            loadPatients();
          }}
        />
      )}
    </div>
  );
};

export default ClinicsPatientsPage;
